import React, {
  useState,
  useRef,
  useEffect,
  ReactNode,
  FocusEvent,
  MouseEvent as ReactMouseEvent, // 👈 Переименовываем React-тип, чтобы избежать конфликтов
  KeyboardEvent as ReactKeyboardEvent,
  ReactElement,
} from 'react';
import './DropDown.scss';

interface DropDownProps {
  customTrigger?: (props?: any) => ReactNode;
  triggerContent?: ReactElement;
  children: ReactNode;
  callback?: (state: boolean) => void;
  defaultState?: boolean;
  closeOnFocusLeave?: boolean
}

export const DropDown: React.FC<DropDownProps> = ({ triggerContent, children, callback, customTrigger, defaultState = false, closeOnFocusLeave = true }) => {
  const [isOpen, setIsOpen] = useState(!!defaultState);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // 👇 Используем globalThis.MouseEvent для нативных DOM-событий
  useEffect(() => {
    const handleClickOutside = (event: globalThis.MouseEvent) => {
      if (closeOnFocusLeave && wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [closeOnFocusLeave]);

  const lastIsOpen = useRef(isOpen)

  useEffect(() => {

    if (lastIsOpen.current !== isOpen) {
      if (callback)
        callback(isOpen)
      lastIsOpen.current = isOpen
    }
  }, [isOpen, callback])

  const handleBlur = (e: FocusEvent<HTMLDivElement>) => {
    const target = e.relatedTarget as Node | null;
    if (!target || !wrapperRef.current?.contains(target)) {
      setIsOpen(false);
    }
  };

  const handleKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      wrapperRef.current?.querySelector<HTMLDivElement>('.drop-down__button')?.focus();
    }
  };

  // 👇 Используем ReactMouseEvent (переименованный) для событий React
  const handleToggle = (e: ReactMouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    setIsOpen((prev) => {
      const next = !prev;
      if (next) {
        wrapperRef.current?.focus();
      }
      return next;
    });
  };

  return (
    <div
      className="drop-down"
      ref={wrapperRef}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
    // tabIndex={-1} // Если раскоментировать, то при фокусе у кастомных триггеров с полями ввода будет теряться фокус из поля ввода
    >

      {customTrigger ? customTrigger({
        className: "drop-down__button",
        onClick: handleToggle,
        "aria-expanded": isOpen,
        "aria-haspopup": "true"
      })
        : <div
          className="drop-down__button"
          onClick={handleToggle}
          aria-expanded={isOpen}
          aria-haspopup="true"
        >
          {triggerContent}
        </div>
      }

      {isOpen && (
        <div className="drop-down__menu" role="menu">
          {children}
        </div>
      )}
    </div>
  );
};